import type { FunctionComponent } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { LiquidityCalculator, LiquidityOverview, useLiquidityOverviewActivated } from '../liquidity-planner';
import { useUserDetails } from './api';
import { PartnerByBrUuidLayout, SessionLayout, WealthPlanningLayout } from './components/routing';
import {
    CalculatorsScreen,
    ContentAdminRoutes,
    DashboardActiveSession,
    DashboardNoActiveSession,
    DocumentsScreen,
    ErrorScreen,
    FamilyTreeScreen,
    LifeEventsScreen,
    LiquidityPlannerScreen,
    ProjectionScreen,
    RoadmapScreen,
    SessionListScreen,
    TopicScreen,
    WealthScreen,
} from './container';
import { useWealthPlanningNavigate } from './hooks/useWealthPlanningNavigate';

export const AppRoutes: FunctionComponent = () => {
    const { data: user } = useUserDetails();
    const { clientUuid } = useWealthPlanningNavigate();
    // todo: (liquidity-activated) remove once feature is stable and officially released
    const { data: isLiquidityPlannerActivated } = useLiquidityOverviewActivated();

    const partnerRoutes = (
        <>
            <Route path=":partnerUuid" element={<SessionLayout />}>
                <Route index={true} element={<Navigate to="topics" replace={true} />} />
                <Route path="topics">
                    <Route index={true} element={<DashboardActiveSession />} />
                    <Route path=":topicId" element={<TopicScreen />} />
                </Route>
                <Route path="events" element={<LifeEventsScreen />} />
                <Route path="wealth" element={<WealthScreen />} />
                <Route path="scenarios" element={<ProjectionScreen />} />
                {isLiquidityPlannerActivated ? (
                    <Route path="liquidity-planner" element={<LiquidityPlannerScreen />}>
                        <Route index={true} element={<LiquidityOverview />} />
                        <Route path="overview" element={<LiquidityOverview />} />
                        <Route path="calculator" element={<LiquidityCalculator />} />
                        <Route path="*" element={<Navigate to=".." replace={true} />} />
                    </Route>
                ) : null}
                <Route path="family-tree" element={<FamilyTreeScreen />} />
                <Route path="roadmap" element={<RoadmapScreen />} />
                <Route path="calculators" element={<CalculatorsScreen />} />
                <Route path="documents">
                    <Route index={true} element={<DocumentsScreen />} />
                    <Route path=":packageId" element={<DocumentsScreen />} />
                </Route>
            </Route>
            <Route path="error" element={<ErrorScreen />} />
            <Route path="permission-denied" element={<ErrorScreen />} />
        </>
    );

    return user?.currentRole === 'CONTENT' ? (
        <ContentAdminRoutes />
    ) : (
        <Routes>
            {!clientUuid ? <Route index={true} element={<Navigate to="/wealth-planning" />} /> : null}
            <Route path="/client-data/:clientUuid/:env/wealth-planning/:businessRelationshipUuid" element={<PartnerByBrUuidLayout />}>
                {partnerRoutes}
            </Route>
            <Route path="wealth-planning" element={<WealthPlanningLayout />}>
                <Route index={true} element={<Navigate to="topics" replace={true} />} />
                <Route path="topics">
                    <Route index={true} element={<DashboardNoActiveSession />} />
                    <Route path=":topicId" element={<TopicScreen />} />
                </Route>
                <Route path="sessions" element={<SessionListScreen />} />
                <Route path="calculators" element={<CalculatorsScreen />} />
                {partnerRoutes}
            </Route>
        </Routes>
    );
};
